import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useRef, useState } from 'react';
import { TReview } from '@/types';
import { clampRating, FILTERED_REVIEWS_MAX_RATING, REVIEWS_MIN_RATING, REVIEWS_PAGE_SIZE } from './ReviewRating.helper';
import { reviewsLineStyles, pointerStyles } from './ReviewRating.styles';
import ReviewsDialogOrDrawer from './ReviewsDialogOrDrawer';
import ReviewStars from './ReviewStars';
import { useAppContext, useFacilityContext } from '@shared-components/context';
import { getBffReviewsSelection } from '@shared-components/services/getBffReviewsSelection';
export interface IReviewRatingProps {
  averageRating?: number | string | null;
  reviewCount?: number | null;
  textColor?: string;
  reviews?: TReview[];
  facilityName?: string;
  /** When provided and `reviews` is empty, reviews are fetched lazily on click */
  facilityCode?: string;
}
export default function ReviewRating({
  averageRating,
  reviewCount,
  textColor,
  reviews,
  facilityName,
  facilityCode
}: Readonly<IReviewRatingProps>) {
  const {
    environment,
    config,
    appName,
    appVersion
  } = useAppContext();
  const {
    facilityBrand
  } = useFacilityContext();
  const rating = clampRating(Number(averageRating ?? 0));
  const count = reviewCount ?? 0;
  const countText = `${count} ${count === 1 ? 'Review' : 'Reviews'}`;
  const [open, setOpen] = useState(false);
  const [loadedReviews, setLoadedReviews] = useState<TReview[]>(reviews ?? []);
  const [take, setTake] = useState(reviews?.length ? reviews.length : 0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(!reviews?.length && count > 0);
  const loadingRef = useRef(false);
  const theme = useTheme();
  const mobileWidth = useMediaQuery(theme.breakpoints.down('md'));
  const url = environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
  const fetchReviews = async (nextTake: number) => {
    if (loadingRef.current || !facilityCode || !url) return;
    loadingRef.current = true;
    setLoading(true);
    const data = await getBffReviewsSelection(facilityBrand, facilityCode, REVIEWS_MIN_RATING, FILTERED_REVIEWS_MAX_RATING, nextTake, url, {
      appName,
      appVersion
    });
    const result = (data?.reviewsSelection?.filter(Boolean) ?? []) as TReview[];
    setLoadedReviews(result);
    setTake(nextTake);
    // fewer than requested => no more reviews on the server
    setHasMore(result.length >= nextTake && result.length < count);
    setLoading(false);
    loadingRef.current = false;
  };
  const handleOpenDialog = () => {
    setOpen(true);
    if (take === 0 && count > 0) void fetchReviews(REVIEWS_PAGE_SIZE); // first open only
  };
  const handleLoadMore = () => void fetchReviews(take + REVIEWS_PAGE_SIZE);
  const handleCloseDialog = () => setOpen(false);
  if (count <= 0) return null;
  return <>
			<Box sx={reviewsLineStyles} data-testid="location_card_reviews" data-sentry-element="Box" data-sentry-source-file="ReviewRating.tsx">
				<Typography data-testid="location_card_average_rating" variant="bodySmallBook" color={textColor} data-sentry-element="Typography" data-sentry-source-file="ReviewRating.tsx">
					{rating.toFixed(1)}
				</Typography>
				<ReviewStars rating={rating} data-sentry-element="ReviewStars" data-sentry-source-file="ReviewRating.tsx" />
				<Typography component="button" type="button" data-testid="location_card_review_count" variant="bodySmallBook" color={textColor} onClick={handleOpenDialog} sx={{
        ...pointerStyles,
        background: 'none',
        border: 0,
        p: 0
      }} data-sentry-element="Typography" data-sentry-source-file="ReviewRating.tsx">
					{countText}
				</Typography>
			</Box>
			<ReviewsDialogOrDrawer open={open} mobileWidth={mobileWidth} handleCloseDialog={handleCloseDialog} reviews={loadedReviews} facilityName={facilityName} loading={loading} hasMore={hasMore} onLoadMore={handleLoadMore} data-sentry-element="ReviewsDialogOrDrawer" data-sentry-source-file="ReviewRating.tsx" />
		</>;
}